import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, ViewChild, AfterViewInit, Injector, OnChanges, SimpleChanges, OnDestroy } from '@angular/core';
import { LL, BOOT_LOCALE } from 'i18n/i18n.service';
import { TranslatedString } from 'models/translated-string';
import { formatWeekdayLong, formatWeekdayDateMed } from '@cf-shared/date-format';
import { FitnessPlan } from '@cf-shared/data-model/fitness-plan';
import { CsCardListComponent } from 'components/cs-card-list/cs-card-list';
import { UiDisplayNutrient } from 'models/display-nutrient';
import { NutrientDisplayMode } from 'components/nutrient-list/nutrient-list';
import { DateTime } from 'luxon';
import { NavController } from 'providers/nav-controller';
import { PopupMenuProvider, PopupMenuButton } from 'providers/popup-menu/popup-menu';
import { DatabaseWrapper , DocWatcher } from 'services/database-wrapper';
import { DebugService } from 'services/debug.service';
import { ExerciseDbService } from 'services/exercise-db.service';
import { TelemetryService } from 'services/telemetry.service';
import { FoodDbService } from 'services/food-db.service';
import { FoodReadFailedError, FoodReadFailurePolicy } from 'services/food-read-error';
import { showFoodLoadFailedAlertAsync } from 'services/food-load-failed-alert';
import { AlertController } from 'providers/alert-controller';
import { LoggingService, isTransientFetchError, errorReportFields } from 'services/logging.service';
import { isDatabaseTornDown } from 'services/database-unavailable-error';
import { describeErrorForLog } from '@cf-shared/error-describe';
import { CoachRelationship, coachRelAsCoachId } from '@cf-shared/data-model/coach';
import { SettingsService } from 'services/settings.service';
import { UserDb } from 'services/user-db.service';
import { SvPlanHandlerService } from 'services/server/sv-plan-handler.service';
import { isPlanCheckFailed } from 'services/server/plan-check-failure';
import { NotificationService } from 'services/notification.service';
import { CheckForPlanAdjustmentsResult, PlanUpdateUserMessage } from '@cf-shared/plan-update-interface';
import { FormatNutrients, formatMeasure, formatTime, getMeasureUnitConversion, formatRelativeDay, formatScheduledMealName, formatWorkoutName } from 'util/formatting';
import { Exercise, getExerciseName } from 'services/exercise-db.service';
import { getExerciseForSubSlot } from 'services/exercise-switching';
import { resolveDisplaySubSlot } from 'util/historical-subslot';
import { reportDisplaySubSlotMissingFromPlan } from 'util/orphan-subslot-reporter';
import { LoadingController } from 'providers/loading-controller';
import { AddMacrosPopover } from 'components/add-macros-popover/add-macros-popover';
import { ReschedulePopover, ReschedulePopoverParams, SchedulableLogRecordType } from 'components/reschedule-popover/reschedule-popover';
import { RescheduleMode } from 'services/schedule-rule.service';
import { PopoverController } from 'providers/popover-controller';
import { Gender } from '@cf-shared/enums';
import { calculateNutrientMatch_pc, FoodStatus } from '@cf-shared/data-model/food';
import { FoodsPageResult } from 'pages/foods/foods-interface';
import { Ingredient, IngredientType, IngredientReal, IngredientMacro } from '@cf-shared/data-model/ingredient';
import { IMealPhotoScannerPage, MealPhotoScannerResult, mealPhotoScanMealParams } from 'pages/meal-photo-scanner-page/meal-photo-scanner-interface';
import { IEditIngredientPage } from 'pages/edit-ingredient/edit-ingredient-interface';
import { buildLogPortionScreenParams } from 'pages/edit-ingredient/log-mode-open';
import { resolveAnyActiveWeeklyRule, Schedule_ItemType } from '@cf-shared/data-model/schedule';
import { Log_DayStart } from '@cf-shared/data-model/log-day-start';
import { Log_Meal, pickOneMealPerSlot } from '@cf-shared/data-model/log-meal';
import { Log_WeighIn, getAverageWaist, getAverageCalipersSum } from '@cf-shared/data-model/log-weigh-in';
import { estimateWeeklyWeightChangeForDisplay, WeightTrendKind } from '@cf-shared/weight-trend';
import { Log_Workout } from '@cf-shared/data-model/log-workout';
import { Log_Entry, LogRecordType, LogRecordState, getEntryLocalTimeInDesiredTimeZone } from '@cf-shared/data-model/user-log';
import { StatusIconTone } from '../cs-status-icon/cs-status-icon';
import { PlanMerge, planMergeForMode, WorkoutLoaderService, WorkoutLoadMode } from 'services/workout-loader.service';
import { FitnessState, ImmutableState } from '@cf-shared/data-model/user-state';
import { CsDayItemsInterface, ScheduleItem } from './cs-day-items-interface';
import { TextDbService } from 'services/text-db.service';
import { ENGLISH_TEXT_SEGMENT } from 'services/text-locale';
import { isoTimeToHours } from '@cf-shared/time';
import { waitForAngularUpdate, DestroyGuard } from 'services/browser-utils';
import { roundToPrecision } from '@cf-shared/shared-util';
import { HomePage } from 'pages/home/home';
import { HomeTab, IHomePage } from 'pages/home/home-interface';
import { MealSuggestionsPageParams, IMealSuggestionsPage, MealSuggestionsMode } from 'pages/meal-suggestions/meal-suggestions-interface';
import { IMealPlanPage } from 'pages/meal-plan/meal-plan-interface';
import { IIntakeFormPage } from 'pages/intake-form/intake-form-interface';
import { IWorkoutDetailPage } from 'pages/workout-detail/workout-detail-interface';
import { getPageInterface } from 'app/app-page-helpers';
import { MealTunerMeal } from '@cf-shared/meal-tuner-interface';
import { WorkoutStateService } from 'services/workout-state.service';
import { MealActionsService } from 'services/meal-actions.service';
import { getMyApp } from 'app/app-interface';
import { IChangelogPage } from 'pages/changelog/changelog-interface';
import { getReadableAppVersion } from 'environments/version-getters';
import { IWeeklyCheckupPage } from 'pages/weekly-checkup/weekly-checkup-interface';
import { IEditWorkoutsPage } from 'pages/edit-workouts/edit-workouts-interface';
import { Log_WeeklyCheckup, WeeklyCheckupCompletionAction } from '@cf-shared/data-model/log-weekly-checkup';
import { VersionCheckService } from 'services/version-check.service';
import { CsCoachBillingNotice } from './cs-day-items-coach-billing-notice';
import { CsUpdateAvailableNotice } from './cs-day-items-update-available-notice';
import { isExpectedChangelogReadFailure } from 'services/changelog-read-failure';
import { CsWhatsNewNotice } from './cs-day-items-whats-new-notice';
import { CsMemberBillingNotice } from './cs-day-items-member-billing-notice';
import { CsPrivacyUpdateNotice } from './cs-day-items-privacy-update-notice';
import { accountConsentsChangedEvent, openTermsReviewEvent, PubSubService } from 'services/pubsub.service';
import { DayItemsNoticeController } from './cs-day-items-notice-host';
import { AuthService } from 'services/auth.service';
import { ICoachBillingPage } from 'pages/coach-billing/coach-billing-page-interface';
import { IUserProfilePage } from 'pages/user-profile/user-profile-interface';
import { DayItemsExerciseReadFailureAction, DayItemsLoadFailureAction, DayItemsLoadOutcome, decideDayItemsExerciseReadFailure, decideDayItemsLoadFailure, runDeferredDayItemsReloadAsync } from './cs-day-items-load-recovery';
import { DayItemsNotice, decideVisibleNotices } from './cs-day-items-notice-stack';
import { DayItemActionKind, getDayItemActionKinds } from './day-item-actions-policy';
import { readMealIngredients } from 'util/meal-ingredients';
import { isCapacitorNative } from 'environments/environment-helpers';
import { App as CapApp } from '@capacitor/app';
import { EntryStepRunner, untrackedEntryStep } from 'providers/page-lifecycle-guard';
// `PluginListenerHandle` is declared in @capacitor/core; @capacitor/app
// imports it for its `addListener` return-type but does NOT re-export it.
import type { PluginListenerHandle } from '@capacitor/core';

@Component({
    selector: 'cs-day-items',
    templateUrl: 'cs-day-items.html',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CsDayItemsComponent implements CsDayItemsInterface, AfterViewInit, OnChanges, OnDestroy {
  protected LL = LL;
  @Input() loading: boolean = true;
  @Input('skip-animation') skipAnimation = false;
  showSpinner = false;
  animateContent = false;
  private spinnerDelayTimer: ReturnType<typeof setTimeout> | null = null;
  @Input('auto-expand') autoExpand = true;
  @Input('use-linear-nutrients') useLinearNutrients = false;
  @Input('display-mode') displayMode: NutrientDisplayMode = NutrientDisplayMode.Logged;
  private docWatcher: DocWatcher;
  // #4110: true while a deferred schedule reload (armed by a failed load during initial
  // sync) is pending — prevents arming a second one in parallel
  private loadRecoveryArmed = false;
  // #6162: counts loadScheduleAsync calls, so the deferred reload can report the outcome of
  // the newest load (what the Daily Log shows) rather than of its own, possibly superseded one
  private scheduleLoadGeneration = 0;
  // the newest load's outcome; undefined while that load is still running
  private newestScheduleLoadOutcome: DayItemsLoadOutcome | undefined = undefined;
  // persistent watcher on the shared changelog doc so the "What's new"
  // card appears reactively when a newer changelog syncs in after the
  // initial dashboard load. Created on first use by the changelog reader
  // below, and released by that notice's dispose.
  private changelogWatcher?: DocWatcher;
  @ViewChild(CsCardListComponent, { static: false }) cardList: CsCardListComponent;
  schedule: ScheduleItem[];
  atToday: boolean;
  currentDay: DateTime;
  dayEntry: Log_DayStart;
  plan: FitnessPlan;
  fitnessState: FitnessState;
  mealPlanPageQueued = false;
  private pendingPositionUpdate = false;
  private destroyGuard = new DestroyGuard();

  private get isFutureDay(): boolean {
    return this.currentDay > this.debug.getNowLocal().startOf('day');
  }

  // Properties for weigh-in warning
  weightDaysAgo: number = 0;
  hasPreviousWeightLog: boolean = false;

  get showWeightWarning(): boolean {
    return this.hasPreviousWeightLog && this.weightDaysAgo > 1; // Warn if more than 1 day since last weight
  }

  get weightWarningMessage(): string {
    if (this.weightDaysAgo > 30) {
      return LL.dayItems.warnings.longTimeSinceLastWeighIn();
    } else if (this.weightDaysAgo >= 14) {
      return LL.dayItems.warnings.weeksSinceLastWeighIn({ weeks: Math.floor(this.weightDaysAgo / 7) });
    } else {
      return LL.dayItems.warnings.daysSinceLastWeighIn({ days: this.weightDaysAgo });
    }
  }

  // Properties for weigh-in success message
  weightProgressAmount: number = 0; // absolute value of weight change
  weightProgressDirection: 'up' | 'down' = 'down';
  weightProgressUnit: string = 'lb';
  showWeightProgress: boolean = false;
  // true when there are too few weigh-ins to state a weekly trend, so the card
  // shows an "undecided" message instead of a (possibly wrong) number (#4306)
  showWeightTrendUndecided: boolean = false;
  isOnTrackWithProgress: boolean = true; // default to success/on-track
  isProgressIndeterminate: boolean = false;

  // Properties for plan update warning
  get showPlanUpdateWarning(): boolean {
    return this.plan?.needsReplan ?? false;
  }

  // Cached next workout date and workout for skipped workout message and no workout card
  nextWorkoutDate: DateTime | null = null;
  nextWorkout: Log_Workout | null = null;
  get nextWorkoutDayName(): string { return this.nextWorkoutDate ? formatWeekdayLong(this.nextWorkoutDate, BOOT_LOCALE) : ''; }
  // full localized date for the skipped-workout message (was an Angular | date pipe). #4204
  get nextWorkoutDateFullLabel(): string { return this.nextWorkoutDate ? formatWeekdayDateMed(this.nextWorkoutDate, BOOT_LOCALE) : ''; }

  // Properties for pending workout warning
  showPendingWorkoutWarning: boolean = false;
  pendingWorkoutDateLabel: string = '';
  pendingWorkoutDateISO: string = '';

  // Properties for unfinished weekly checkup warning
  showUnfinishedWeeklyCheckupWarning: boolean = false;
  unfinishedWeeklyCheckupDateLabel: string = '';
  unfinishedWeeklyCheckupId: string = '';

  // Properties for first workout reminder
  showFirstWorkoutReminder: boolean = false;
  firstWorkoutDayName: string = '';
  firstWorkoutReminderDismissed: boolean = false;

  // Capacitor App plugin listener handle for appStateChange (resume
  // refresh trigger). Owned so ngOnDestroy can unsubscribe — Capacitor
  // listeners persist across navigations otherwise.
  private appStateListener: PluginListenerHandle | null = null;

  // Watches the coach's roster singleton so a late-arriving document re-runs the
  // coach billing card's decision. Created on first use by the roster reader the
  // notice is given below, and released by that notice's dispose.
  private coachRosterWatcher: DocWatcher | null = null;

  /**
   * The notice cards that own their own state (#5236).
   *
   * Each holds its own fields, its own refresh, its own dismiss and action
   * handlers and whatever it needs to release, instead of that being spread
   * across this class as a `showX` boolean plus companion fields plus a refresh
   * method plus handlers. `noticeControllers` below is what `ngOnDestroy` walks,
   * so a notice added here is released without anyone remembering to say so.
   *
   * Which of them actually renders is still decided by `decideVisibleNotices` —
   * see `showsNotice`. Not every notice has moved yet; the rest are still plain
   * fields on this class.
   *
   * The two billing notices can never describe the SAME failed payment: the
   * member rule requires a `Paddle` licence type, so anyone whose own membership
   * is paid by a coaching subscription is excluded. They are NOT mutually
   * exclusive in general, because the coach card fires on the mere presence of a
   * coach block — somebody carrying a residual coach block while paying for their
   * own Paddle membership can see both, and those are two different
   * subscriptions rather than two copies of one message. See
   * cs-day-items-member-billing-card.ts, "the coach trap".
   */
  protected readonly notices: {
    coachBilling: CsCoachBillingNotice;
    memberBilling: CsMemberBillingNotice;
    updateAvailable: CsUpdateAvailableNotice;
    whatsNew: CsWhatsNewNotice;
    privacyUpdate: CsPrivacyUpdateNotice;
  };

  /** Every notice above, as the one thing `ngOnDestroy` needs from them. */
  private get noticeControllers(): readonly DayItemsNoticeController[] {
    return Object.values(this.notices);
  }

  LogRecordType = LogRecordType;
  LogRecordState = LogRecordState;
  StatusIconTone = StatusIconTone;
  // the notice-card names the template asks about, see showsNotice below
  protected readonly Notice = DayItemsNotice;

  /**
   * Which of the notice cards above the day's item list is drawn right now.
   *
   * The list of cards, the order they are drawn in and the rules about which of
   * them may appear together all live in cs-day-items-notice-stack.ts. This
   * gathers each card's own "do I have something to say" answer and asks. The
   * `showX` fields it reads are still set by each card's own refresh method — only
   * the decision about which of them actually renders moved out of the template.
   */
  protected showsNotice(notice: DayItemsNotice): boolean {
    return decideVisibleNotices({
      atToday: this.atToday,
      wantsToShow: {
        [DayItemsNotice.PrivacyUpdate]: this.notices.privacyUpdate.wantsToShow,
        [DayItemsNotice.CoachBilling]: this.notices.coachBilling.wantsToShow,
        [DayItemsNotice.MemberBilling]: this.notices.memberBilling.wantsToShow,
        [DayItemsNotice.WeightWarning]: this.showWeightWarning,
        [DayItemsNotice.PlanUpdate]: this.showPlanUpdateWarning,
        [DayItemsNotice.PendingWorkout]: this.showPendingWorkoutWarning,
        [DayItemsNotice.UpdateAvailable]: this.notices.updateAvailable.wantsToShow,
        [DayItemsNotice.WhatsNew]: this.notices.whatsNew.wantsToShow,
        [DayItemsNotice.UnfinishedWeeklyCheckup]: this.showUnfinishedWeeklyCheckupWarning,
        [DayItemsNotice.FirstWorkoutReminder]: this.showFirstWorkoutReminder,
      },
    }).includes(notice);
  }

  constructor(
    private debug: DebugService,
    protected userDb: UserDb,
    private settings: SettingsService,
    private logging: LoggingService,
    private foodDb: FoodDbService,
    private exerciseDb: ExerciseDbService,
    private loadingCtrl: LoadingController,
    private cdRef: ChangeDetectorRef,
    private telemetry: TelemetryService,
    private navCtrl: NavController,
    private popupMenuProvider: PopupMenuProvider,
    private popoverCtrl: PopoverController,
    private injector: Injector,
    private svPlanHandler: SvPlanHandlerService,
    private workoutLoader: WorkoutLoaderService,
    private workoutStateService: WorkoutStateService,
    private mealActionsService: MealActionsService,
    private notificationService: NotificationService,
    private versionCheck: VersionCheckService,
    private textDb: TextDbService,
    private auth: AuthService,
    private alertCtrl: AlertController,
    private pubSub: PubSubService,
  ) {
    // built here rather than as a field initializer so every injected service is
    // already assigned when a notice captures it
    this.notices = {
      coachBilling: new CsCoachBillingNotice({
        host: this.noticeHost,
        isCoachManaging: () => this.isCoachManaging(),
        getCoach: () => this.auth.couch,
        getUid: () => this.auth.user?.uid,
        canManageInApp: () => this.auth.getHasCoachSeat(),
        now: () => DateTime.utc(),
        locale: BOOT_LOCALE,
        roster: this.coachRosterReader,
        trackSilentProblem: (name, context) => this.logging.trackSilentProblem(name, context),
        openCoachBillingPage: () => { void this.navCtrl.pushAsync(ICoachBillingPage, {}); },
      }),
      updateAvailable: new CsUpdateAvailableNotice({
        host: this.noticeHost,
        versionCheck: this.versionCheck,
        wordings: {
          downloading: () => LL.app.updateAvailable.downloading(),
          ready: () => LL.app.updateAvailable.ready(),
          available: () => LL.app.updateAvailable.message(),
          restartAction: () => LL.app.updateAvailable.restart(),
          updateAction: () => LL.app.updateAvailable.action(),
          unsupported: () => LL.app.updateRequired.cardMessage(),
        },
        trackAppUpdateEvent: (action, version, belowMinimum) =>
          this.telemetry.trackAppUpdateEvent(action, 'card', version, belowMinimum),
        trackAbnormalException: (err) => this.logging.trackAbnormalException(err),
        onWhatsNewReclaimSlotAsync: () => this.notices.whatsNew.reclaimSlotAsync(),
      }),
      whatsNew: new CsWhatsNewNotice({
        host: this.noticeHost,
        changelog: this.changelogReader,
        flags: {
          getShowChangelogNotification: () => getMyApp().showChangelogPageNotification,
          setShowChangelogNotification: (value) => { getMyApp().showChangelogPageNotification = value; },
          getChangelogRev: () => getMyApp().changelogRev,
          setChangelogRev: (rev) => { getMyApp().changelogRev = rev; },
        },
        readableAppVersion: () => getReadableAppVersion(),
        trackSilentProblem: (name, context) => this.logging.trackSilentProblem(name, context),
        trackWhatsNewOpened: (version) => this.telemetry.trackEvent('app_update_whats_new', { version }),
        openChangelogPage: () => { void this.navCtrl.pushAsync(IChangelogPage, {}); },
      }),
      memberBilling: new CsMemberBillingNotice({
        host: this.noticeHost,
        isCoachManaging: () => this.isCoachManaging(),
        getMember: () => this.auth.couch,
        getUid: () => this.auth.user?.uid,
        now: () => DateTime.utc(),
        locale: BOOT_LOCALE,
        openMembershipPage: () => { void this.navCtrl.pushAsync(IUserProfilePage, {}); },
      }),
      privacyUpdate: new CsPrivacyUpdateNotice({
        host: this.noticeHost,
        getConsents: () => this.auth.couch?.consents,
        isCoachManaging: () => this.isCoachManaging(),
        now: () => DateTime.utc(),
        openTermsReview: () => this.pubSub.publish(openTermsReviewEvent, undefined),
        watchConsentsChanged: (onChanged) => {
          const registration = this.pubSub.subscribe(accountConsentsChangedEvent, onChanged);
          return () => registration.unsubscribe();
        },
        trackCardAction: (action) => this.telemetry.trackEvent('consent_update_card', { action }),
      }),
    };
  }

  /**
   * The two abilities a notice may ask of this component: whether the view is
   * gone, and that it needs re-rendering. Deliberately not the `DestroyGuard` or
   * the `ChangeDetectorRef` themselves — a notice that imported either would pull
   * in `@angular/core` and lose the mocha unit-test path. See
   * cs-day-items-notice-host.ts.
   */
  private readonly noticeHost = {
    isDestroyed: () => this.destroyGuard.isDestroyed,
    markForCheck: () => this.cdRef.markForCheck(),
  };

  /**
   * Reads the changelog document and the profile revision the "What's new" gate
   * compares it against, and owns the watcher that read registers.
   *
   * Watched, not read once: a newer changelog syncing in after the dashboard has
   * loaded then re-runs the gate, so the card appears without waiting for a cold
   * start. The watcher is created on the first read and released by the notice's
   * dispose.
   */
  private readonly changelogReader = {
    readChangelogRevAsync: async (onChangelogChanged: () => void): Promise<string> => {
      if (!this.changelogWatcher) {
        this.changelogWatcher = this.textDb.createTextWatcher(() => {
          if (this.destroyGuard.isDestroyed) return;
          onChangelogChanged();
        });
        // register the document before the read: a read that throws registers
        // nothing, and the card's recovery depends on this watcher being woken
        // when the document arrives (#6245)
        this.textDb.watchTextByHelpId('app-version-changelog', this.changelogWatcher, ENGLISH_TEXT_SEGMENT);
      }
      // pin English: this read is only used for its `_rev`, which is the identity
      // the "what's new" gate compares against the synced `profile.lastAppChangelog`.
      // Per-locale documents have unrelated revision lineages, so a localized rev
      // would re-fire the card on every language switch and never agree across
      // devices.
      const changelog = await this.textDb.getTextByHelpIdAsync(
        'app-version-changelog', this.changelogWatcher, ENGLISH_TEXT_SEGMENT);
      return changelog._rev;
    },
    // which failures the card stays quiet about, and what brings it back after
    // one: see isExpectedChangelogReadFailure
    isExpectedReadFailure: isExpectedChangelogReadFailure,
    // a profile read or write that failed because of a network drop, or because the
    // database is being torn down (sign-out, reload), is expected; anything else the
    // notice reports (df-9241e82523c2)
    isExpectedProfileFailure: (err: unknown): boolean =>
      isTransientFetchError(err) || isDatabaseTornDown(err),
    readLastSeenRevAsync: async (): Promise<string | undefined> => {
      const profile = await this.userDb.loadProfileStateAsync(this.docWatcher);
      return profile.lastAppChangelog;
    },
    saveLastSeenRevAsync: async (rev: string): Promise<void> => {
      const profile = await this.userDb.loadProfileStateAsync(this.docWatcher);
      if (this.destroyGuard.isDestroyed) return;
      profile.lastAppChangelog = rev;
      await this.userDb.saveProfileStateAsync(profile, DatabaseWrapper.getTransactionId());
    },
    release: (): void => {
      this.changelogWatcher?.destroy();
      this.changelogWatcher = undefined;
    },
  };

  /**
   * Reads the coach's roster for the coach billing notice, and owns the watcher
   * that read registers.
   *
   * Watched, not read once. The roster changes while the app runs (a revision synced
   * in from another device reaches the in-memory copy and wakes the watcher). Without
   * a watcher a coach whose roster changed would keep the count read at view init and
   * the card would silently show the old sentence for the whole session. (Until #6248
   * this also covered a boot read that fell back to the `{}` default; start-up no
   * longer carries on with such a default.)
   *
   * The watcher is created on the FIRST read, never before: registering one puts
   * it on that user's home screen for the rest of the session, and the notice only
   * reads once its card is known to render and to have something to say about
   * clients. Any read failure travels up to the notice, which reports it — this
   * does not catch.
   */
  private readonly coachRosterReader = {
    countClients: (onRosterChanged: () => void): number => {
      if (!this.coachRosterWatcher) {
        this.coachRosterWatcher = this.userDb.database.createDocWatcher(() => {
          if (this.destroyGuard.isDestroyed) return;
          onRosterChanged();
        });
      }
      const roster = this.userDb.database.getSingleton(coachRelAsCoachId, this.coachRosterWatcher) as CoachRelationship;
      return roster?.clients?.length ?? 0;
    },
    release: (): void => {
      this.coachRosterWatcher?.destroy();
      this.coachRosterWatcher = null;
    },
  };

  markForCheck() {
    this.cdRef.markForCheck();
  }

  /**
   * Recalculate a meal's nutrition and say whether the meal may now be saved.
   *
   * Returns false when one of the meal's foods could not be READ — a connection blip, or the
   * local database being busy. `recalculateMealNutrientsAsync` throws in that case instead of
   * quietly leaving that ingredient out of the total, so saving now would store a number we
   * already know is too low. The user is told it is a connection problem, and the caller must
   * put the meal back the way it found it and stop.
   */
  private async tryRecalculateMealNutrientsAsync(meal: Log_Meal): Promise<boolean> {
    try {
      await this.foodDb.recalculateMealNutrientsAsync(meal);
      return true;
    } catch (e) {
      if (e instanceof FoodReadFailedError) {
        // stay silent while signing out: the read fails because the database is being torn down,
        // which is expected teardown rather than a connection problem the user can act on
        // (the "Sign-out race convention" in design-docs/warm-resume.md)
        if (!this.destroyGuard.isDestroyed && !this.auth.isLikelySignOutRace()) {
          await showFoodLoadFailedAlertAsync(this.alertCtrl);
        }
        return false;
      }
      throw e;
    }
  }

  async ngAfterViewInit() {
    // first-show refresh of the "Update available" card (PRD §C4).
    // fire-and-forget — the service throttles the actual backend
    // query and the await-isReady inside getStatusAsync handles the
    // PWA SW boot-time race, so the card resolves naturally once
    // the data arrives without blocking the dashboard render. the
    // .catch surfaces backend failures (Phase A android stub
    // throws "not yet implemented"; later phases will throw on
    // transient Play-Core failures) so they don't become silent
    // unhandled rejections — per CLAUDE.md "unexpected conditions
    // must surface".
    this.notices.updateAvailable.refreshAsync('startup')
      .catch(err => this.logging.trackAbnormalException(err));

    // #4393 Surface B — the coach's pre-lapse warning. Synchronous: it reads
    // the login result and the in-memory roster singleton, so there is nothing
    // to await. This is the FIRST evaluation, not the only one: the licence
    // half only changes on a fresh sign-in (which re-creates this component),
    // but the roster half can arrive later, so the notice's roster reader watches
    // it and re-runs this.
    this.notices.coachBilling.refresh();

    // df-6f0a2f8274a3 — the member's own payment-failing warning. Synchronous
    // for the same reason as the coach card, and simpler: it reads the login
    // result only, with no roster to wait for, so this is the ONLY evaluation
    // it needs. The licence half changes on a fresh sign-in, which re-creates
    // this component.
    this.notices.memberBilling.refresh();

    // #5587 — "we updated the Privacy Policy". Synchronous and read-only like the
    // two above: it asks the shared consent-refresh rule about the cached login
    // result. This is the FIRST evaluation, not the only one: accepting the new
    // text happens on a screen that opens OVER this dashboard, so the login
    // result changes while this component stays alive. The notice listens for
    // that itself (`watchConsentsChanged` above) and re-runs this.
    this.notices.privacyUpdate.refresh();

    // native-only resume subscription (PRD §C5). the Capacitor App
    // plugin is the cross-platform source of "user brought the app
    // back to the foreground"; on web the browser's
    // visibilitychange would be the equivalent, but we don't need
    // it here — PWA users hit the dashboard via a real navigation
    // (or the SW reload after Update tap), which already triggers a
    // fresh ngAfterViewInit and re-runs the startup refresh above.
    if (isCapacitorNative()) {
      const handle = await CapApp.addListener('appStateChange', state => {
        if (this.destroyGuard.isDestroyed) return;
        if (state.isActive) {
          this.notices.updateAvailable.refreshAsync('resume')
            .catch(err => this.logging.trackAbnormalException(err));
        }
      });
      // if the component was destroyed while addListener's promise
      // was pending, ngOnDestroy already ran with
      // appStateListener === null so its remove() no-oped. detach
      // the just-resolved handle ourselves so the listener doesn't
      // orphan in Capacitor's registry until page reload.
      if (this.destroyGuard.isDestroyed) {
        void handle.remove();
      } else {
        this.appStateListener = handle;
      }
    }

    // don't wait if component is already destroyed
    if (!this.destroyGuard.isDestroyed) {
      await waitForAngularUpdate(this.injector);
      // if there was a pending position update from before the view was ready, execute it now
      if (this.pendingPositionUpdate && this.cardList) {
        this.cardList.updatePositions();
        this.pendingPositionUpdate = false;
      }
    }
  }

  async ngOnChanges(changes: SimpleChanges) {
    // handle spinner delay logic
    if (changes['loading']) {
      if (changes['loading'].currentValue === true) {
        // reset animation flag when loading starts
        this.animateContent = false;
        // only start spinner timer if skipAnimation is false (i.e., not leaving the page)
        if (!this.skipAnimation) {
          this.spinnerDelayTimer = this.destroyGuard.safeTimeout(() => {
            this.showSpinner = true;
            this.cdRef.markForCheck();
          }, 300);
        }
      } else {
        // loading finished - clear timer and hide spinner
        if (this.spinnerDelayTimer) {
          this.destroyGuard.cancelTimeout(this.spinnerDelayTimer);
          this.spinnerDelayTimer = null;
        }
        // only animate if spinner was shown and skipAnimation is false
        this.animateContent = this.showSpinner && !this.skipAnimation;
        this.showSpinner = false;
      }
    }

    // when loading changes from true to false, the cs-card-list will be rendered
    // we need to wait for Angular to update the view and then update positions
    if (changes['loading'] && !changes['loading'].currentValue && changes['loading'].previousValue === true) {
      // don't wait if component is destroyed
      if (!this.destroyGuard.isDestroyed) {
        await waitForAngularUpdate(this.injector);
        // if there's a pending update and cardList is now available
        if (this.pendingPositionUpdate && this.cardList) {
          this.cardList.updatePositions();
          this.pendingPositionUpdate = false;
        }
      }
    }
  }

  ngOnDestroy() {
    this.docWatcher?.destroy();
    // release whatever each notice created — its own watcher, its own listener.
    // one loop, so a notice added to `notices` cannot be forgotten here.
    for (const notice of this.noticeControllers) {
      notice.dispose();
    }
    // detach the Capacitor App listener before flipping the
    // destroyGuard — if an appStateChange event arrives in the
    // small window between the two, the inner-callback isDestroyed
    // check would short-circuit (correct) but the listener handle
    // would still be live at event-dispatch time. removing first
    // closes that window. consistent with sibling cleanup paths
    // that detach listeners before tearing down per-instance state.
    void this.appStateListener?.remove();
    this.appStateListener = null;
    this.destroyGuard.destroy();
  }

  getScheduledItems(): ScheduleItem[] {
    return this.schedule;
  }

  // this is used so we can re-load with same params. before the first load there is nothing
  // to re-load, so the placeholder answers Loaded (nothing is left unbuilt)
  reloadScheduleAsync: () => Promise<DayItemsLoadOutcome> = async () => DayItemsLoadOutcome.Loaded;

  /**
   * Loads the log entries around the given day and builds the Daily Log's schedule items.
   * A transient network failure of the load (possible while the initial sync routes reads
   * to the remote CouchDB) is logged and healed by a one-shot deferred reload once the sync
   * settles (#4110), which reports its own result (#6162); any other error propagates to the
   * caller. Answers whether the Daily Log was built, which is how that reload knows.
   */
  async loadScheduleAsync(mode: 'all' | 'workout' | 'meals', now: DateTime, currentDay: DateTime, dayDisplayNutrients: UiDisplayNutrient[] | undefined = undefined, steps: EntryStepRunner = untrackedEntryStep): Promise<DayItemsLoadOutcome> {

    // lazy-init DocWatcher so synced log entry changes trigger a targeted item update
    if (!this.docWatcher) {
      this.docWatcher = this.userDb.database.createDocWatcher((changes) => {
        if (this.destroyGuard.isDestroyed) return undefined;
        // #4358: RETURN it, never `void` it — the watcher uses the promise to
        // keep two updates from overlapping during a sync burst. Filtering by
        // id (below) reduces how MUCH each run does, but on its own it never
        // stopped two runs being in flight at once.
        return this.updateChangedItemsAsync(changes.map(c => c.id));
      }, { label: 'cs-day-items' });
    }

    // a reload is never part of a page entry, so it does not pass `steps` on
    this.reloadScheduleAsync = () => this.loadScheduleAsync(mode, now, currentDay, dayDisplayNutrients);

    // #6162: this load is now the newest; its outcome is recorded only if no newer one starts
    const generation = ++this.scheduleLoadGeneration;
    this.newestScheduleLoadOutcome = undefined;
    const settle = (outcome: DayItemsLoadOutcome) => {
      if (generation === this.scheduleLoadGeneration) this.newestScheduleLoadOutcome = outcome;
      return outcome;
    };

    //let s = await this.userDb.loadScheduleAsync();

    // let entries = await this.svPlanHandler.generateBacklogInMemoryAsync(currentDay, currentDay, s.days, {});

    try {
      const currentDayDate = currentDay.toISODate();
      let entries = await steps('day.dayEntries', () => this.userDb.getLogEntriesInRangeAsync(
        currentDay.minus({ days: 1 }).startOf('day'),
        currentDay.plus({ days: 1 }).endOf('day'),
        this.docWatcher,
        { inLogEntriesTimeZone: true }
      ));

      entries = entries.filter(e => getEntryLocalTimeInDesiredTimeZone(e, currentDay).toISODate() === currentDayDate);

      entries = entries.sort((a, b) => getEntryLocalTimeInDesiredTimeZone(a, now).toISO() <= getEntryLocalTimeInDesiredTimeZone(b, now).toISO() ? -1 : 1);

      await this.createScheduleItems(mode, entries, dayDisplayNutrients, now, currentDay, steps);
    } catch (err) {
      // #4110: on a device that has not yet finished its first download, these reads go to
      // the remote CouchDB during initial sync (pass-through; #5068 took the returning-device
      // case back out), so a brief network blip can fail exactly this load. before this handler, that one failure
      // blanked the Daily Log permanently: `this.schedule` was never assigned, and the doc
      // watcher callback rebuilds nothing when there is no schedule yet. so for a transient
      // network error, swallow it and reload once the sync settles.
      // #6162: only log it here. a dropped request is expected at this point and the
      // recovery below reports whether the Daily Log came back, which is what matters.
      const decision = decideDayItemsLoadFailure(err, this.loadRecoveryArmed);
      if (decision.action === DayItemsLoadFailureAction.Rethrow) throw err;

      const database = this.userDb.database;
      const passThroughActive = () => database.getReadDb() !== database.getWriteDb();
      console.log('DayItems: load deferred after a dropped request', JSON.stringify({
        ...errorReportFields(err),
        passThroughActive: passThroughActive(),
        recoveryAlreadyPending: !decision.armRecovery,
      }));

      if (decision.armRecovery) {
        // the flag is cleared only by the runner's onDone, so at most one recovery is
        // ever pending — a successful load in between must not reset it, or a later
        // failure could arm a second parallel runner
        this.loadRecoveryArmed = true;
        // subscribe to the sync's outcomes rather than handing over its
        // `whenInitialSyncCompleted` promise: the retry loops replace that field once per
        // round, so the promise belonging to the sync that was running when the read failed
        // stops being the one that ever speaks again (#4435)
        void runDeferredDayItemsReloadAsync({
          subscribeToInitialSyncSettled: listener => database.onInitialSyncSettled(listener),
          isDestroyed: () => this.destroyGuard.isDestroyed,
          clearBacklogCacheAsync: () => this.userDb.clearBacklogCacheAsync(),
          reloadAsync: async () => {
            await this.reloadScheduleAsync();
            this.destroyGuard.safeMarkForCheck(this.cdRef);
            // report what the Daily Log shows: the user may have changed day while this
            // reload's read was in flight, and then the newest load decides (#6162)
            return this.newestScheduleLoadOutcome ?? DayItemsLoadOutcome.NewerLoadInProgress;
          },
          trackAbnormalException: (e) => this.logging.trackAbnormalException(e),
          onRecovered: () => {
            this.logging.trackSilentInfo('DayItems: load recovered after transient network', {
              passThroughActive: passThroughActive(),
            });
          },
          onReloadFailedAgain: () => {
            // no "(transient network)" suffix on purpose: the user is looking at an empty
            // Daily Log, so this counts for session health (session-health.ts)
            this.logging.trackSilentProblem('DayItems: deferred reload failed, Daily Log empty', {
              passThroughActive: passThroughActive(),
            });
          },
          onDone: () => { this.loadRecoveryArmed = false; },
        });
      }
      return settle(DayItemsLoadOutcome.DeferredAfterDroppedRequest);
    }

    if (this.mealPlanPageQueued) {
      void this.navCtrl.pushAsync(IMealPlanPage, {});
      this.mealPlanPageQueued = false;
    }
    return settle(DayItemsLoadOutcome.Loaded);
  }

  /**
   * Reactive sync: selectively updates only the schedule items whose backing log entries changed.
   * Instead of rebuilding the entire schedule, finds the matching ScheduleItem by doc ID and
   * regenerates just its display text/nutrients/measurements.
   */
  private async updateChangedItemsAsync(changedDocIds: string[]) {
    if (!this.schedule) return;
    const changedSet = new Set(changedDocIds);
    let updated = false;

    for (const item of this.schedule) {
      if (!item.entry?._id || !changedSet.has(item.entry._id)) continue;

      const e = item.entry;
      if (e.type === LogRecordType.Meal) {
        [item.text, item.nutrients] = await this.logging.safeAsync(
          () => this.foodDb.generateMealTextAsync(e as Log_Meal, this.docWatcher), ["Meal error", []]
        );
        updated = true;
      } else if (e.type === LogRecordType.Workout) {
        item.text = await this.logging.safeAsync(
          () => this.generateWorkoutTextAsync(e as Log_Workout), "Workout error"
        );
        updated = true;
      } else if (e.type === LogRecordType.WeighIn) {
        const immutableState = await this.userDb.loadImmutableStateAsync(this.docWatcher);
        const w = e as Log_WeighIn;
        item.text = await this.logging.safeAsync(
          () => this.generateWeighinTextAsync(w, immutableState.gender), "Weigh-in error"
        );
        const locale = this.settings.locale;
        item.weightValue = w.weight_kg ? formatMeasure(w.weight_kg, 'bodyweight_kg', locale, w.weight_unit) : '--';
        const waistAvg = getAverageWaist(w);
        item.waistValue = waistAvg ? formatMeasure(waistAvg, 'bodydimensions_cm', locale, w.tapeMeasures?.unit) : '--';
        const calipersAvg = getAverageCalipersSum(w, immutableState.gender);
        item.skinfoldsValue = calipersAvg ? formatMeasure(calipersAvg, 'skinfold_mm', locale, 'mm') : '--';
        updated = true;
      } else if (e.type === LogRecordType.DayStart) {
        // DayStart nutrients are handled by the parent via dayDisplayNutrients reference
        updated = true;
      }
    }

    if (updated) {
      // guard it: the watcher callback checks the destroy guard only on entry, and the
      // awaited reads above give the view time to be torn down before we get here
      this.destroyGuard.safeMarkForCheck(this.cdRef);
    }
  }

  async createScheduleItems(
    mode: 'all' | 'workout' | 'meals',
    entries: Log_Entry[],
    dayDisplayNutrients: UiDisplayNutrient[] | undefined, // only needed if one of the entries is DayStart
    now: DateTime,
    currentDay: DateTime,
    // #4988: times each read below on its own when this load is part of Home's entry
    steps: EntryStepRunner = untrackedEntryStep,
  ) {

    this.atToday = currentDay.hasSame(now, 'day');
    this.currentDay = currentDay;
    this.dayEntry = null;
    let schedule: ScheduleItem[] = [];

    // find next workout date for skipped workout messages and no workout card (look 7 days ahead)
    this.nextWorkoutDate = null;
    this.nextWorkout = null;
    const futureEntries = await steps('day.futureEntries', () => this.userDb.getLogEntriesInRangeAsync(
      currentDay.plus({ days: 1 }).startOf('day'),
      currentDay.plus({ days: 7 }).endOf('day'),
      this.docWatcher,
      { inLogEntriesTimeZone: true }
    ));
    const nextWorkout = futureEntries.find(entry =>
      entry.type === LogRecordType.Workout && entry.state !== LogRecordState.Skipped
    ) as Log_Workout;
    if (nextWorkout) {
      this.nextWorkoutDate = getEntryLocalTimeInDesiredTimeZone(nextWorkout, now);
      this.nextWorkout = nextWorkout;
    }

    // find pending workout from past days for warning card
    // check if today's workout is in SoftFuture mode (meaning there's an unfinished past workout)
    // only show on training and home pages, not on nutrition page
    // only show if pending workout was actually started (Incomplete), not just scheduled
    this.showPendingWorkoutWarning = false;
    if (this.atToday && mode !== 'meals') {
      const todayWorkout = entries.find(entry => entry.type === LogRecordType.Workout) as Log_Workout;
      if (todayWorkout) {
        const result = await steps('day.workoutMode', () => this.workoutLoader.getWorkoutModeWithContextAsync(todayWorkout, this.userDb, now, this.docWatcher));
        if (result.mode === WorkoutLoadMode.SoftFuture && result.currentWorkout && result.currentWorkout.state === LogRecordState.Incomplete) {
          const todayDay = now.startOf('day');
          const currentWorkoutDay = getEntryLocalTimeInDesiredTimeZone(result.currentWorkout, now).startOf('day');
          this.pendingWorkoutDateLabel = formatRelativeDay(todayDay, currentWorkoutDay);
          this.pendingWorkoutDateISO = currentWorkoutDay.toISODate();
          this.showPendingWorkoutWarning = true;
        }
      }
    }

    // find unfinished weekly checkup from past days for warning card
    // only show when viewing today
    this.showUnfinishedWeeklyCheckupWarning = false;
    if (this.atToday && mode !== 'meals') {
      // look for unfinished weekly checkups from the past 7 days (excluding today)
      const searchStart = currentDay.minus({ days: 7 }).startOf('day');
      const searchEnd = currentDay.minus({ days: 1 }).endOf('day');
      const pastEntries = await steps('day.pastEntries', () => this.userDb.getLogEntriesInRangeAsync(searchStart, searchEnd, this.docWatcher, { inLogEntriesTimeZone: true }));
      const unfinishedCheckup = pastEntries.find(entry =>
        entry.type === LogRecordType.WeeklyCheckup &&
        entry.state !== LogRecordState.Done &&
        entry.state !== LogRecordState.Skipped
      );
      if (unfinishedCheckup) {
        // suppress warning if the next weekly checkup is within 3 days —
        // the old one becomes less relevant and showing it feels like a duplicate
        const schedule = await steps('day.checkupSchedule', () => this.userDb.loadScheduleAsync());
        // whichever weekly check-up rule is in force today — asked this way so the
        // answer does not depend on the order the rules are stored in
        const checkupRule = resolveAnyActiveWeeklyRule(
          schedule.rules ?? [], Schedule_ItemType.WeeklyCheckup, now.toISODate());
        if (checkupRule?.weekday) {
          const daysUntilNext = (checkupRule.weekday - now.weekday + 7) % 7 || 7;
          if (daysUntilNext <= 3) {
            // next checkup is soon — don't nag about the old one
          } else {
            const todayDay = now.startOf('day');
            const checkupDay = getEntryLocalTimeInDesiredTimeZone(unfinishedCheckup, now).startOf('day');
            this.unfinishedWeeklyCheckupDateLabel = formatRelativeDay(todayDay, checkupDay);
            this.unfinishedWeeklyCheckupId = unfinishedCheckup._id;
            this.showUnfinishedWeeklyCheckupWarning = true;
          }
        }
      }
    }

    // check for "What's new" changelog notification (post-upgrade
    // nudge — "here's what changed in your current version"). Done
    // reactively: a persistent watcher on the changelog doc re-runs the
    // gate when a newer changelog syncs in after this initial load, so
    // the card appears without waiting for the next cold start.
    await steps('day.whatsNew', () => this.notices.whatsNew.refreshAsync());

    this.plan = await steps('day.plan', () => this.userDb.loadPlanAsync(this.docWatcher));
    this.fitnessState = await steps('day.fitnessState', () => this.userDb.loadFitnessStateAsync(this.docWatcher));
    let immutableState = await steps('day.immutableState', () => this.userDb.loadImmutableStateAsync(this.docWatcher));

    if (mode === 'workout') {
      entries = entries.filter(e => e.type === LogRecordType.Workout);
    } else if (mode === 'meals') {
      entries = entries.filter(e => e.type === LogRecordType.Meal);
    } else {
      entries = entries.filter(
        e =>
          e.type == LogRecordType.DayStart ||
          e.type == LogRecordType.WeighIn ||
          e.type == LogRecordType.Workout ||
          e.type == LogRecordType.Meal ||
          e.type == LogRecordType.DayEnd ||
          e.type == LogRecordType.WeeklyCheckup
      );
    }

    // if nutrition is completely disabled
    if (!this.userDb.nutritionTracking && !this.userDb.nutritionTargets) {
      // don't show meals
      entries = entries.filter(e => e.type !== LogRecordType.Meal);
    }

    // collapse to at most one entry per event slot per day, so the same event never renders
    // twice (never two WeighIns, two of a Meal index, two Workouts, two wake-ups/sleeps, etc.).
    // The storage layer normally keeps exactly one per event, but a rare cross-device duplicate
    // (two filled entries for one event after a reschedule) must never surface as two cards.
    // Prefer an entry that holds user data; among equals keep the latest (entries are already
    // sorted ascending by time). "Holds user data" is `logEntryHoldsUserData`, which asks a
    // workout's CONTENT rather than its state field — reading the field alone hid a finished
    // session behind an empty placeholder (#4840). See design-docs/sync-integrity.md.
    // Meals go through the SHARED rule (pickOneMealPerSlot), because the day's calorie total is
    // summed with that same rule — if the two ever disagree the header shows a number that the
    // cards below it do not add up to, which is exactly what a customer reported in #4728.
    const mealsOnDay = entries.filter(e => e.type === LogRecordType.Meal) as Log_Meal[];
    const mealsToShow = new Set<Log_Entry>(pickOneMealPerSlot(mealsOnDay, currentDay).kept);

    // Everything else is a one-per-day event, so its slot is simply its type. That rule also
    // lives in the SHARED helper, because the Nutrition page's day card asks the same question
    // about the same day and used to answer it differently — see pickOneEntryPerSingletonSlot
    // and #4511. Asked through UserDb rather than called directly, because that wrapper also
    // reports the document it set aside, which nothing did before (df-97a26a40a3e0).
    const singletonsToShow = new Set<Log_Entry>(this.userDb.keepOneEntryPerSingletonSlot(entries, currentDay));
    entries = entries.filter(e =>
      e.type === LogRecordType.Meal ? mealsToShow.has(e) : singletonsToShow.has(e),
    );

    // reset the "it has been N days since your last weigh-in" card's two values before the
    // loop below, which is the only thing that writes them — the same way the pending-workout,
    // unfinished-checkup and first-workout cards each reset above the code that may set them.
    // Without this the loop can skip the weigh-in entry while the day on screen is still today
    // (the entry is hidden by the homepage-customization setting, or the day has none at all),
    // and the card would go on drawing a day count worked out during an earlier load. A person
    // who opens the app with the weigh-in row already hidden sees no card at all, so this is
    // also what makes the two cases agree.
    this.hasPreviousWeightLog = false;
    this.weightDaysAgo = 0;

    // #4988: the per-entry work (texts, nutrients, weigh-in progress, state fixes) timed as one part
    await steps('day.buildItems', () => this.buildScheduleItemsAsync(entries, schedule, dayDisplayNutrients, now, currentDay, immutableState));

    // if no workout and it's today and we have next workout info, show "No Workout" card
    // (only if workout display is enabled in homepage customization)
    const hasWorkout = schedule.some(item => item.entry.type === LogRecordType.Workout);
    if ((mode === 'workout' || mode === 'all') && !hasWorkout && this.atToday && this.nextWorkout && this.nextWorkoutDate && this.settings.homepageShowWorkout) {
      let item = {} as ScheduleItem;
      item.id = 'NoWorkout';
      item.title = LL.dayItems.titles.restDay();
      item.targetData = {};
      item.entry = {} as Log_Entry;
      const nextWorkoutLocalTime = getEntryLocalTimeInDesiredTimeZone(this.nextWorkout, now);
      const time = currentDay.set({
        hour: this.nextWorkoutDate.hour,
        minute: this.nextWorkoutDate.minute
      })
      item.entry.time_utc = time.toISO();
      item.hourId = isoTimeToHours(nextWorkoutLocalTime.toISOTime());
      item.time = formatTime(nextWorkoutLocalTime, this.settings);
      schedule.push(item);
    }

    // first workout reminder for new users on a rest day
    this.showFirstWorkoutReminder = false;
    if (
      this.atToday &&
      !this.firstWorkoutReminderDismissed &&
      (mode === 'workout' || mode === 'all') &&
      !hasWorkout &&
      this.plan?.ready === true &&
      this.nextWorkoutDate &&
      Object.keys(this.fitnessState?.exerciseStats ?? {}).length === 0
    ) {
      this.showFirstWorkoutReminder = true;
      this.firstWorkoutDayName = formatWeekdayLong(this.nextWorkoutDate, BOOT_LOCALE);
    }

    // if this is really the current day
    if (this.atToday) {
      // find the first incomplete item
      for (let item of schedule) {
        let e = item.entry;
        if (e.state !== LogRecordState.Done && e.state !== LogRecordState.Skipped && this.isFoldable(item)) {
          // and expand it
          item.expanded = this.autoExpand; // (only if allowed)
          item.highlighted = true; // highlight always
          break;
        }
      }
    }

    this.schedule = schedule.sort((a, b) => {
      return a.hourId <= b.hourId ? -1 : 1;
    });

    // don't wait for Angular updates if component is destroyed
    if (!this.destroyGuard.isDestroyed) {
      await steps('day.angularUpdate', () => waitForAngularUpdate(this.injector));

      // update positions if cardList is available, otherwise mark it as pending
      if (this.cardList) {
        this.cardList.updatePositions();
      } else {
        // cardList is not yet available (probably because loading is still true)
        // mark that we need to update positions once it becomes available
        this.pendingPositionUpdate = true;
      }
    }
  }

  // builds one schedule item per entry of the day and appends it to `schedule`. Its own
  // method so the day load can time it as one part (#4988) without nesting it deeper.
  private async buildScheduleItemsAsync(
    entries: Log_Entry[],
    schedule: ScheduleItem[],
    dayDisplayNutrients: UiDisplayNutrient[] | undefined,
    now: DateTime,
    currentDay: DateTime,
    immutableState: ImmutableState,
  ): Promise<void> {
    for (let e of entries) {
      if (e.type == LogRecordType.DayStart && dayDisplayNutrients) {
        if (this.debug.isBeforeNow_utc(e.time_utc) && e.state !== LogRecordState.Done) {
          await this.updateLogEntryStateAsync(e, LogRecordState.Done);
        }
        this.dayEntry = e as Log_DayStart;
        // we need to adjust the array passed in as the parameter, can't create a new object, so clear, and push
        dayDisplayNutrients.splice(0, dayDisplayNutrients.length);
        dayDisplayNutrients.push(...FormatNutrients(this.dayEntry.nutrients, this.userDb, { targets: this.dayEntry.targets }));
        if (!this.settings.showDayStartEvents) {
          continue;
        }
      }

      if (this.debug.isBeforeNow_utc(e.time_utc) && e.type === LogRecordType.DayEnd && e.state !== LogRecordState.Done) {
        await this.updateLogEntryStateAsync(e, LogRecordState.Done);
      }

      if (e.type === LogRecordType.DayEnd && !this.settings.showDayEndEvents) {
        continue;
      }

      // homepage customization: filter items based on user settings
      if (e.type === LogRecordType.WeighIn && !this.settings.homepageShowWeighIn) {
        continue;
      }
      if (e.type === LogRecordType.Workout && !this.settings.homepageShowWorkout) {
        continue;
      }
      if (e.type === LogRecordType.Meal && !this.settings.homepageShowMeals) {
        continue;
      }

      let item = {} as ScheduleItem;
      item.entry = e;
      // compute hourId for sorting
      const entryLocalTime = getEntryLocalTimeInDesiredTimeZone(e, now);
      item.hourId = isoTimeToHours(entryLocalTime.toISOTime());
      // translate generated meal names like "Meal 1"; a custom name the user
      // typed (Breakfast, Lunch, …) passes through unchanged
      item.title = formatScheduledMealName(e.name);
      let time = formatTime(entryLocalTime, this.settings);
      item.time = time;
      item.expanded = false;
      item.highlighted = false;

      if (e.type === LogRecordType.Workout) {
        item.id = 'Workout';
        // use effective workout name (handles deprecated name field that may be undefined).
        // The stored name is canonical English, so translate it for display.
        item.title = formatWorkoutName(this.workoutLoader.getEffectiveWorkoutName(e as Log_Workout, this.plan))
          || LL.training.workout();
        item.text = await this.logging.safeAsync(() => this.generateWorkoutTextAsync(e as Log_Workout), "Workout error");
        item.target = 'WorkoutDetailPage';
        item.targetData = { id: e._id };
        if (e.state !== this.isWorkoutIncomplete(e)) {
          await this.updateLogEntryStateAsync(e, LogRecordState.Incomplete);
        }
      } else if (e.type === LogRecordType.WeighIn) {
        item.id = 'Weigh-in';
        item.title = LL.dayItems.titles.weighIn();
        item.text = await this.logging.safeAsync(() => this.generateWeighinTextAsync(e as Log_WeighIn, immutableState.gender), "Weigh-in error");
        // Store individual values for icon display
        const w = e as Log_WeighIn;
        const locale = this.settings.locale;
        item.weightValue = w.weight_kg ? formatMeasure(w.weight_kg, 'bodyweight_kg', locale, w.weight_unit) : '--';
        const waistAvg = getAverageWaist(w);
        item.waistValue = waistAvg ? formatMeasure(waistAvg, 'bodydimensions_cm', locale, w.tapeMeasures?.unit) : '--';
        const calipersAvg = getAverageCalipersSum(w, immutableState.gender);
        item.skinfoldsValue = calipersAvg ? formatMeasure(calipersAvg, 'skinfold_mm', locale, 'mm') : '--';
        item.target = 'EditWeighInPage';
        item.targetData = { id: e._id };

        // Calculate days since last weigh-in and weight progress if this entry has weight data
        if (this.atToday) {
          // Replicate logic from edit-weigh-in page's determineCountdowns method
          const todayBoD_local = currentDay.startOf('day');
          const recentWeighIns = await this.userDb.getRecentLogEntries<Log_WeighIn>(LogRecordType.WeighIn, todayBoD_local, 31);
          const recentWeighInsReversed = recentWeighIns.reverse();

          // Helper function to check if log contains weight
          const logContainsWeight = (weighIn: Log_WeighIn) => {
            return weighIn.weight_kg && weighIn.weight_kg > 0;
          };

          // Find latest weigh-in with weight data
          const latestWeightLog = recentWeighInsReversed.find(logContainsWeight);

          // Calculate days elapsed since last weight log
          const daysElapsedSinceLog = (logEntry: Log_WeighIn) =>
            Math.round(todayBoD_local.diff(getEntryLocalTimeInDesiredTimeZone(logEntry, todayBoD_local), 'days').as('days'));

          const weightDaysAgo = latestWeightLog ? daysElapsedSinceLog(latestWeightLog) : 99;

          // Set component properties for warning display
          this.hasPreviousWeightLog = latestWeightLog !== undefined;
          this.weightDaysAgo = weightDaysAgo;

          // Calculate weight progress if today's entry has weight
          if (w.weight_kg && w.weight_kg > 0) {
            await this.calculateWeightProgress(currentDay, w);
          }
        }
      } else if (e.type === LogRecordType.Meal) {
        item.id = e.name.replace(/ /g, ''); // convert name to id by removing spaces
        [item.text, item.nutrients] = await this.logging.safeAsync(() => this.foodDb.generateMealTextAsync(e as Log_Meal, this.docWatcher), ["Meal error", []]);
        item.target = 'EditMealPage';
        item.targetData = { id: e._id };
        if (e.state === LogRecordState.Done && this.ingredientsOf(e as Log_Meal).length === 0) {
          await this.updateLogEntryStateAsync(e, LogRecordState.Scheduled);
        }
      } else if (e.type === LogRecordType.WeeklyCheckup) {
        item.id = 'WeeklyCheckup';
        item.title = LL.dayItems.titles.weeklyCheckup();
        item.text = this.isAwaitingCoachCheckup(e as Log_WeeklyCheckup)
          ? LL.coach.checkup.awaitingReview()
          : LL.dayItems.text.reviewYourWeek();
        item.target = 'WeeklyCheckupPage';
        item.targetData = { id: e._id };
      } else {
        item.id = e.type === LogRecordType.DayEnd ? 'DayEnd' : 'DayStart';
        item.title = e.type === LogRecordType.DayEnd
          ? LL.dayItems.titles.sleep()
          : LL.dayItems.titles.wakeUp();
        //item.text, item.nutrients] = await this.generateMealText(e as Log_Meal);
        item.target = '';
        item.targetData = {};
      }

      schedule.push(item);
    }
  }

  async updateLogEntryStateAsync(entry: Log_Entry, newState: LogRecordState) {
    entry.state = newState;

    let transaction = DatabaseWrapper.getTransactionId();
    try {
      await this.userDb.saveEntryAsync(transaction, entry);
    } catch (err) {
      // if conflict, refetch fresh entry and retry
      if (err.message?.includes('conflicts with externally synchronized')) {
        const freshEntry = await this.userDb.getOneLogEntryAsync(entry._id, this.docWatcher);
        if (freshEntry && freshEntry.state !== newState) {
          freshEntry.state = newState;
          await this.userDb.saveEntryAsync(transaction, freshEntry);
        }
        // if freshEntry already has correct state, no action needed
      } else {
        throw err;
      }
    }
    if (entry.type == LogRecordType.Meal) {
      await this.userDb.updateDayValuesOnDiskAsync(transaction, getEntryLocalTimeInDesiredTimeZone(entry, this.debug.getNowLocal()));
    }
    // user advanced the entry to Done (eaten) or Skipped — either way they will not act on this
    // reminder, so suppress today's matching reminder. This fires whenever an entry reaches this
    // setter with a Done state (main callers: `handleDoneMealButton`, the day-roll auto-complete of
    // past DayStart entries, and the auto-complete of past DayEnd entries) or with a Skipped state
    // (`handleSkipButton`'s Meal case and its default WeighIn / WeeklyCheckup branch). The
    // Skipped case is #4087 — before it, a skipped meal kept getting its "time to eat" reminder.
    // The DayEnd Done path is intentional: design-docs/push-notifications.md notes DayEnd has no
    // per-event hook, but when it does flip to Done (via auto-complete) we may as well suppress the
    // matching server schedule and dismiss any leftover tray entry. fire-and-forget;
    // suppressForEntryAsync is a no-op for entry types with no NOTIFICATION_CONFIG (e.g. DayStart).
    if (newState === LogRecordState.Done || newState === LogRecordState.Skipped) {
      void this.notificationService.suppressForEntryAsync(entry, this.debug.getNowLocal());
    }
  }

  /**
   * Whether the day list's summary of this workout merges its stored exercises with the current
   * plan (#6574). The workout page decides this from the workout's mode; the day list has no mode,
   * so it asks for one only where the answer can differ: an unfinished workout with a stored list
   * on a day before today. Asking costs a database read, which every other case skips.
   */
  private async planMergeForDayItemAsync(w: Log_Workout): Promise<PlanMerge> {
    // a finished or skipped workout is never merged, and one with no stored list is built from the plan
    if (w.state === LogRecordState.Done || w.state === LogRecordState.Skipped || !w.exercises?.length) {
      return PlanMerge.WithCurrentPlan;
    }
    const now = this.debug.getNowLocal();
    // a workout from today onwards will be trained under today's plan
    if (getEntryLocalTimeInDesiredTimeZone(w, now).startOf('day') >= now.startOf('day')) {
      return PlanMerge.WithCurrentPlan;
    }
    const result = await this.workoutLoader.getWorkoutModeWithContextAsync(w, this.userDb, now, this.docWatcher);
    return planMergeForMode(result.mode);
  }

  async generateWorkoutTextAsync(w: Log_Workout): Promise<string> {
    let texts = [];
    const planMerge = await this.planMergeForDayItemAsync(w);
    for (let e of this.workoutLoader.getEffectiveExercises(w, this.plan, planMerge)) {
      // take the exercise from the workout - in case it was already performed
      let exerciseId = e.exerciseId;

      let exerciseDb = this.exerciseDb;
      let userDb = this.userDb;
      let fitnessState = this.fitnessState;
      let plan = this.plan;
      const nowUTC = this.debug.getNowLocal().toUTC();

      // #4614: fall back to the snapshot the workout log carries — or, for a session logged
      // before snapshots existed, to a sub-slot rebuilt from the rest of the log — when the
      // program no longer holds this exercise, so removing one in Edit Workouts cannot erase it
      // from the summary of a session the user already performed. resolveDisplaySubSlot owns
      // the rule.
      const resolved = resolveDisplaySubSlot(this.plan.subSlots, e);
      if (resolved.missingFromPlan) {
        reportDisplaySubSlotMissingFromPlan(
          e.subSlotId, resolved.source, this.logging, 'generateWorkoutText', e);
      }
      let planSubSlot = resolved.subSlot;
      if (!planSubSlot) {
        continue;
      }

      // if this one exercise could not be read (a timed-out catalog fetch, say), show '?' for
      // its name alone: a stand-in would name a different exercise from the one in the plan,
      // and letting the failure travel would replace the whole line (df-a89f1ea2145e)
      let exercise: Exercise | undefined;
      try {
        exercise = await getExerciseForSubSlot(planSubSlot, exerciseId, exerciseDb, userDb, fitnessState, plan, nowUTC, this.docWatcher);
      } catch (err) {
        // a dropped request is only logged (df-b0163737f620), our own database teardown is not a
        // fault, and every other failure is reported
        const action = decideDayItemsExerciseReadFailure(err);
        if (action === DayItemsExerciseReadFailureAction.Report) {
          this.logging.trackSilentProblem('DayItemsExerciseReadFailed', { subSlotId: e.subSlotId, error: describeErrorForLog(err) });
        } else if (action === DayItemsExerciseReadFailureAction.Log) {
          console.log('DayItems: exercise read dropped', JSON.stringify({ subSlotId: e.subSlotId, ...errorReportFields(err) }));
        }
      }

      const exerciseName = getExerciseName(exercise);

      texts.push(exerciseName);
    }

    return texts.join(', ');
  }

  async generateWeighinTextAsync(w: Log_WeighIn, gender: Gender): Promise<string> {
    let locale = this.settings.locale;
    let weightText = w.weight_kg ? `${formatMeasure(w.weight_kg, 'bodyweight_kg', locale, w.weight_unit)}` : '--';
    let waistAvg = getAverageWaist(w);
    let waistText = waistAvg ? `${formatMeasure(waistAvg, 'bodydimensions_cm', locale, w.tapeMeasures.unit)}` : '--';
    let calipersAvg = getAverageCalipersSum(w, gender);
    let calipersText = calipersAvg ? `${formatMeasure(calipersAvg, 'skinfold_mm', locale, 'mm')}` : '--';
    return 'Weight ' + weightText + '&nbsp;&nbsp;Waist ' + waistText + '&nbsp;&nbsp;Skinfolds ' + calipersText;
  }

  isFoldable(item: ScheduleItem) {
    return item.entry.type !== LogRecordType.DayStart && item.entry.type !== LogRecordType.DayEnd && item.entry.type !== undefined;
  }

  isItemDone(itemState: LogRecordState) {
    return itemState === LogRecordState.Done;
  }

  isItemIncomplete(itemState: LogRecordState) {
    return itemState === LogRecordState.Incomplete;
  }

  isItemSkipped(itemState: LogRecordState) {
    return itemState === LogRecordState.Skipped;
  }

  getItemTitle(item: ScheduleItem) {
    return item.title;
  }

  isWorkoutIncomplete(workout: Log_Workout) {
    let bAnySetCompleted = false;
    let bAnySetIncompleted = false;
    const exercises = this.workoutLoader.getEffectiveExercises(workout, this.plan, PlanMerge.AsStored);
    for (let exercise of exercises) {
      this.logging.checkWorkoutExerciseValidity("isWorkoutIncomplete", exercise)
      for (let set of exercise.sets) {
        if (!set) {
          continue; // this can happen due to the problem that checkWorkoutExerciseValidity is checking for
        }
        if (set.state === LogRecordState.Done) {
          bAnySetCompleted = true;
        }
        if (set.state === LogRecordState.Incomplete) {
          bAnySetIncompleted = true;
        }
      }
    }

    if (bAnySetCompleted && bAnySetIncompleted) {
      return LogRecordState.Incomplete;
    }

    return workout.state;
  }

  isItemSuccessfullyCompleted(item: ScheduleItem): boolean {
    if (item.entry.state !== LogRecordState.Done) {
      return false;
    }

    if (item.entry.type === LogRecordType.Meal) {
      return !this.shouldShowMealWarning(item.entry);
    }

    return true;
  }

  getItemOpenButtonText(item: ScheduleItem) {
    const defaultText = "Open";
    if (!this.atToday) {
      return defaultText;
    }
    if (item.entry.type === LogRecordType.Workout && item.entry.state === LogRecordState.Incomplete) {
      return "Continue";
    }
    if (item.entry.type === LogRecordType.Meal && item.entry.state === LogRecordState.Scheduled) {
      return "Open";
    }

    return defaultText;
  }


  openItem(e: Event, item: ScheduleItem) {
    e?.stopPropagation();
    if (item.target != null && item.target != '') {
      void this.navCtrl.pushAsync(getPageInterface(item.target), item.targetData);
      this.telemetry.trackEvent('home_item_open', { type: item.entry.type });
    }
  }

  clickedItem(e: Event, item: ScheduleItem) {
    e?.stopPropagation();
    // DayEnd/DayStart - open reschedule popover to edit sleep/wake time
    if (item.entry.type === LogRecordType.DayEnd || item.entry.type === LogRecordType.DayStart) {
      void this.handleRescheduleButton(item, e.target as HTMLElement);
      return;
    }
    // future day workout - navigate to edit-workouts instead of workout-detail
    if (this.isFutureDay && item.entry.type === LogRecordType.Workout) {
      const workout = item.entry as Log_Workout;
      const workoutIndex = this.workoutLoader.getEffectivePlanReference(workout, this.plan);
      void this.navCtrl.pushAsync(IEditWorkoutsPage, { workoutIndex });
      return;
    }
    this.openItem(e, item);
  }

  /**
   * The one way this component reads a meal's ingredient list.
   *
   * A meal that arrives with no list at all is an impossible state — the type
   * says the field is always there — so it is reported rather than quietly
   * tolerated, and repaired rather than allowed to throw. The repair happens
   * inside readMealIngredients and writes an empty list onto the meal, so the
   * report fires exactly once per damaged meal: every later read of the same
   * object finds a list and stays quiet. See client/src/util/meal-ingredients.ts.
   */
  private ingredientsOf(meal: Log_Meal): Ingredient[] {
    const read = readMealIngredients(meal);
    // if the meal had no list, say so once — the repair above keeps later reads quiet
    if (read.wasMissing) {
      this.logging.trackSilentProblem('MealIngredientListMissing', {
        mealId: meal._id,
        mealName: meal.name,
        state: meal.state,
      });
    }
    return read.ingredients;
  }

  shouldShowMealWarning(logMeal: Log_Meal) {
    if (this.ingredientsOf(logMeal).length === 0) {
      return false;
    }
    let match_pc = calculateNutrientMatch_pc(logMeal.nutrients, logMeal.targets);
    if (match_pc < 95) {
      return true;
    }
    return false;
  }

  shouldShowMacrosMatchBadge(logMeal: Log_Meal) {
    if (this.ingredientsOf(logMeal).length === 0) {
      return false;
    }
    let match_pc = calculateNutrientMatch_pc(logMeal.nutrients, logMeal.targets);
    return match_pc >= 95;
  }

  getMealMatchPercentage(meal: Log_Meal): number | undefined {
    if (this.ingredientsOf(meal).length === 0) {
      return undefined;
    }
    return calculateNutrientMatch_pc(meal.nutrients, meal.targets);
  }

  async handleSkipButton(item: ScheduleItem) {
    switch (item.entry.type) {
      case LogRecordType.Meal:
        await this.updateLogEntryStateAsync(item.entry, LogRecordState.Skipped);
        break;
      case LogRecordType.Workout:
        const loading = this.loadingCtrl.create({ content: LL.common.loading.pleaseWait() });
        await loading.present();
        await this.handleWorkoutSkipping(item.entry);
        await loading.dismiss();
        break;
      default:
        await this.updateLogEntryStateAsync(item.entry, LogRecordState.Skipped);
        break;
    }
    this.destroyGuard.safeMarkForCheck(this.cdRef);
    this.telemetry.trackEvent('home_item_skip', { item: item.entry.type });
  }

  async handleDoneMealButton(mealLog: Log_Meal) {
    // suppression of today's meal reminder is handled inside updateLogEntryStateAsync (Done/Skipped branch)
    await this.updateLogEntryStateAsync(mealLog, LogRecordState.Done);
  }

  async handleRevertButton(item: ScheduleItem) {
    switch (item.entry.type) {
      case LogRecordType.Meal:
        if (this.ingredientsOf(item.entry).length > 0) {
          await this.updateLogEntryStateAsync(item.entry, LogRecordState.Planned);
          break;
        }
        await this.updateLogEntryStateAsync(item.entry, LogRecordState.Scheduled);
        break;
      case LogRecordType.Workout:
        const loading = this.loadingCtrl.create({ content: LL.common.loading.pleaseWait() });
        await loading.present();
        await this.handleWorkoutRevertSkipping(item.entry);
        await loading.dismiss();
        break;
      case LogRecordType.WeighIn:
        const weighIn = item.entry as Log_WeighIn;
        // clear all measurements
        delete weighIn.weight_kg;
        delete weighIn.weight_unit;
        delete weighIn.tapeMeasures;
        delete weighIn.caliperMeasures;
        await this.updateLogEntryStateAsync(item.entry, LogRecordState.Scheduled);
        break;
      default:
        await this.updateLogEntryStateAsync(item.entry, LogRecordState.Scheduled);
        break;
    }
    // the user put the entry back, so bring back today's reminder the done/skip silenced — if it
    // has not fired yet. workouts do this inside revertSkipWorkoutAsync instead, because the
    // training page reverts through it too. fire-and-forget.
    if (item.entry.type !== LogRecordType.Workout) {
      void this.notificationService.unsuppressForEntryAsync(item.entry, this.settings, () => this.debug.getNowLocal());
    }
    this.destroyGuard.safeMarkForCheck(this.cdRef);
  }

  async handleWorkoutSkipping(entry: Log_Workout) {
    await this.workoutStateService.skipWorkoutAsync(entry, this.docWatcher);
  }

  async handleWorkoutRevertSkipping(entry: Log_Workout) {
    await this.workoutStateService.revertSkipWorkoutAsync(entry, this.docWatcher);
  }

  shouldShowDoneButton(item: ScheduleItem): boolean {
    // Show button for meals (either "Mark as Complete" or "Add Food")
    if (item.entry.type === LogRecordType.Meal) {
      return item.entry.state !== LogRecordState.Done &&
             item.entry.state !== LogRecordState.Skipped;
    }
    // Show button for weigh-ins without weight data
    if (item.entry.type === LogRecordType.WeighIn) {
      const weighIn = item.entry as Log_WeighIn;
      return !weighIn.weight_kg &&
             item.entry.state !== LogRecordState.Done &&
             item.entry.state !== LogRecordState.Skipped;
    }
    // Show button for workouts
    if (item.entry.type === LogRecordType.Workout) {
      // Show "Resume" for in-progress
      if (item.entry.state === LogRecordState.Incomplete) {
        return true;
      }
      // Show "Start" for scheduled (not started)
      if (item.entry.state === LogRecordState.Scheduled) {
        return true;
      }
      // Show "Done" in test mode for other states
      return this.settings.testMode &&
             item.entry.state !== LogRecordState.Done &&
             item.entry.state !== LogRecordState.Skipped;
    }
    // Show "Start" button for weekly checkup
    if (item.entry.type === LogRecordType.WeeklyCheckup) {
      // AwaitingCoach checkups: show a "Review" button for the managing coach, nothing for the client
      if (this.isAwaitingCoachCheckup(item.entry as Log_WeeklyCheckup)) {
        return this.isCoachManaging();
      }
      return item.entry.state !== LogRecordState.Done &&
             item.entry.state !== LogRecordState.Skipped;
    }
    return false;
  }

  private isAwaitingCoachCheckup(entry: Log_WeeklyCheckup): boolean {
    return entry.completionAction === WeeklyCheckupCompletionAction.AwaitingCoach;
  }

  private isCoachManaging(): boolean {
    return !!this.userDb.getCoachManagingClient();
  }

  getDoneButtonText(item: ScheduleItem): TranslatedString {
    if (item.entry.type === LogRecordType.Meal) {
      const meal = item.entry as Log_Meal;
      if (this.ingredientsOf(meal).length > 0) {
        return LL.dayItems.buttons.markAsComplete();
      } else {
        return LL.dayItems.buttons.addFood();
      }
    }
    if (item.entry.type === LogRecordType.WeighIn) {
      return LL.dayItems.buttons.logWeight();
    }
    if (item.entry.type === LogRecordType.Workout) {
      if (item.entry.state === LogRecordState.Incomplete) {
        return LL.dayItems.buttons.resume();
      }
      if (item.entry.state === LogRecordState.Scheduled) {
        // if in test mode, show "Done" to quickly mark as complete
        if (this.settings.testMode) {
          return LL.dayItems.buttons.done();
        }
        // future day - show "Edit" instead of "Start"
        if (this.isFutureDay) {
          return LL.common.buttons.edit();
        }
        return LL.dayItems.buttons.start();
      }
    }
    if (item.entry.type === LogRecordType.WeeklyCheckup) {
      if (this.isAwaitingCoachCheckup(item.entry as Log_WeeklyCheckup)) {
        return LL.coach.checkup.reviewTitle();
      }
      return LL.dayItems.buttons.start();
    }
    return LL.dayItems.buttons.done(); // For workouts in test mode
  }

  getAdditionalActions(item: ScheduleItem, targetElement: HTMLElement): PopupMenuButton[] {
    // the placeholder has no entry worth reading, so answer before touching it
    if (item.id === 'NoWorkout') {
      return [];
    }

    const { state, type } = item.entry;

    // the item's own date against today (not the day being viewed)
    const now = this.debug.getNowLocal();
    const itemDate = getEntryLocalTimeInDesiredTimeZone(item.entry, now).toISODate();
    const isPastDay = itemDate < now.toISODate();

    // read ingredients only for an open meal, the one case the answer is used:
    // the read repairs and reports a damaged meal, so it must not run for more items than before
    const isOpenMeal = type === LogRecordType.Meal && state !== LogRecordState.Skipped && state !== LogRecordState.Done;
    const mealHasIngredients = isOpenMeal && this.ingredientsOf(item.entry as Log_Meal).length > 0;

    // which actions to offer is decided in day-item-actions-policy.ts, where it is unit-tested
    const kinds = getDayItemActionKinds({
      type,
      state,
      mealHasIngredients,
      isPastDay,
    });

    // for each kind: its translated label and its handler
    return kinds.map(kind => this.buildDayItemActionButton(kind, item, targetElement));
  }

  /** the menu button for one action kind: its translated label and its tap handler */
  private buildDayItemActionButton(kind: DayItemActionKind, item: ScheduleItem, targetElement: HTMLElement): PopupMenuButton {
    switch (kind) {
      case DayItemActionKind.Skip:
        return { text: LL.common.buttons.skip(), handler: () => this.handleSkipButton(item) };
      case DayItemActionKind.LogMacrosManually:
        return {
          text: LL.dayItems.buttons.logMacrosManually(),
          handler: () => this.handleLogMacrosManuallyActionClicked(item, targetElement),
        };
      case DayItemActionKind.AutoSuggestions:
        return { text: LL.ingredientSearch.buttons.autoSuggestions(), handler: () => this.handleMealPlannerActionClicked(item) };
      case DayItemActionKind.ScanMeal:
        return { text: LL.editMeal.buttons.scanMeal(), handler: () => this.handleScanMealClicked(item) };
      case DayItemActionKind.ClearMeal:
        return { text: LL.ingredientSearch.buttons.clearMeal(), handler: () => this.handleClearMealButton(item) };
      case DayItemActionKind.Restore:
        return { text: LL.common.buttons.restoreTask(), handler: () => this.handleRevertButton(item) };
      case DayItemActionKind.Reschedule:
        return { text: LL.dayItems.buttons.reschedule(), handler: () => this.handleRescheduleButton(item, targetElement) };
      case DayItemActionKind.Edit: {
        // resolved when the menu is built, as it always was
        const workoutIndex = this.workoutLoader.getEffectivePlanReference(item.entry as Log_Workout, this.plan);
        return {
          text: LL.common.buttons.edit(),
          handler: () => {
            void this.navCtrl.pushAsync(IEditWorkoutsPage, { workoutIndex });
          },
        };
      }
      default: {
        const unhandled: never = kind;
        throw new Error(`no menu button for day item action ${String(unhandled)}`);
      }
    }
  }

  hasAdditionalActions(item: ScheduleItem): boolean {
    return this.getAdditionalActions(item, null).length > 0;
  }

  async handlePrimaryButtonClick(item: ScheduleItem) {
    if (item.entry.type === LogRecordType.Meal) {
      const meal = item.entry as Log_Meal;
      if (this.ingredientsOf(meal).length > 0) {
        // Mark as complete
        await this.handleDoneMealButton(meal);
      } else {
        // Add food - open the meal page
        this.clickedItem(new Event('click'), item);
      }
    } else if (item.entry.type === LogRecordType.WeighIn) {
      // Log weight - open the weigh-in page
      this.clickedItem(new Event('click'), item);
    } else if (item.entry.type === LogRecordType.Workout) {
      // if in test mode and scheduled, mark as done directly
      if (this.settings.testMode && item.entry.state === LogRecordState.Scheduled) {
        await this.updateLogEntryStateAsync(item.entry, LogRecordState.Done);
      } else if (this.isFutureDay && item.entry.state === LogRecordState.Scheduled) {
        // future day - navigate to edit-workouts page
        const workout = item.entry as Log_Workout;
        const workoutIndex = this.workoutLoader.getEffectivePlanReference(workout, this.plan);
        void this.navCtrl.pushAsync(IEditWorkoutsPage, { workoutIndex });
      } else if (item.entry.state === LogRecordState.Incomplete || item.entry.state === LogRecordState.Scheduled) {
        // Start or Resume workout - open the workout page
        this.clickedItem(new Event('click'), item);
      } else if (this.settings.testMode) {
        // Handle workout done in test mode
        await this.updateLogEntryStateAsync(item.entry, LogRecordState.Done);
      }
    } else if (item.entry.type === LogRecordType.WeeklyCheckup) {
      // Start weekly checkup - open the weekly checkup page
      this.clickedItem(new Event('click'), item);
    }
  }

  shouldShowStatusIndicator(item: ScheduleItem): boolean {
    // Show status indicator for done/skipped states, or in-progress workouts
    if (item.entry.state === LogRecordState.Done || item.entry.state === LogRecordState.Skipped) {
      return true;
    }
    if (item.entry.type === LogRecordType.Workout && item.entry.state === LogRecordState.Incomplete) {
      return true;
    }
    return false;
  }

  onLogWeightClick(event: Event) {
    event.stopPropagation();
    // find the weigh-in item in the schedule
    const weighInItem = this.schedule.find(item => item.entry.type === LogRecordType.WeighIn);
    if (weighInItem) {
      void this.navCtrl.pushAsync(getPageInterface(weighInItem.target), weighInItem.targetData);
      this.telemetry.trackEvent('home_weight_in_warning_link_click', {});
    }
  }

  onUpdateProgramClick(event: Event) {
    event.stopPropagation();
    void this.navCtrl.pushAsync(IIntakeFormPage, { mode: 'change' });
    this.telemetry.trackEvent('home_plan_update_warning_link_click', {});
  }

  onViewProgressClick(event: Event) {
    event.stopPropagation();
    // if the current page is already the home page
    const activePage = this.navCtrl.getActivePageName();
    if (activePage === "HomePage") {
      // directly switch to progress tab
      const homePageInstance = this.navCtrl.getActive().instance as HomePage;
      homePageInstance.selectedTab = HomeTab.Progress;
      this.cdRef.markForCheck();
    } else {
      // navigate to home page with progress tab selected
      void this.navCtrl.setRootAsync(IHomePage, { tab: HomeTab.Progress });
    }
    this.telemetry.trackEvent('home_weight_progress_link_click', {});
  }

  handleStartWorkoutClick() {
    // navigate to workout detail page with date only (no id)
    // workout-details will find the next workout letter
    void this.navCtrl.pushAsync(IWorkoutDetailPage, {
      date: this.currentDay.toISO()
    });
    this.telemetry.trackEvent('home_start_workout_link_click', {});
  }

  onPendingWorkoutWarningDismissed() {
    this.showPendingWorkoutWarning = false;
    this.cdRef.markForCheck();
  }

  navigateToPendingWorkoutDay() {
    // if already on home page, use gotoDateAsync to change the day
    const activePage = this.navCtrl.getActivePageName();
    if (activePage === "HomePage") {
      const homePageInstance = this.navCtrl.getActive().instance as HomePage;
      void homePageInstance.gotoDateAsync(DateTime.fromISO(this.pendingWorkoutDateISO));
    } else {
      void this.navCtrl.setRootAsync(IHomePage, { day: this.pendingWorkoutDateISO });
    }
    this.telemetry.trackEvent('home_pending_workout_link_click', {});
  }

  onUnfinishedWeeklyCheckupWarningDismissed() {
    this.showUnfinishedWeeklyCheckupWarning = false;
    this.cdRef.markForCheck();
  }

  navigateToUnfinishedWeeklyCheckupDay() {
    void this.navCtrl.pushAsync(IWeeklyCheckupPage, { id: this.unfinishedWeeklyCheckupId });
    this.telemetry.trackEvent('home_unfinished_weekly_checkup_link_click', {});
  }

  showActionMenu(event: Event, item: ScheduleItem) {
    event.stopPropagation();

    const targetElement = event.target as HTMLElement;
    const buttons = this.getAdditionalActions(item, targetElement);

    if (buttons.length === 0) {
      return;
    }

    void this.popupMenuProvider.createMenu({
      buttons,
      targetFromElement: targetElement,
    });
  }

  async handleLogMacrosManuallyActionClicked(item: ScheduleItem, targetElement: HTMLElement) {
    const meal = item.entry as Log_Meal;

    // open AddMacrosPopover
    const result = await AddMacrosPopover.present(
      this.popoverCtrl,
      targetElement
    );

    if (result) {
      // create ingredient from macro data
      const newIngredient: IngredientMacro = {
        type: IngredientType.Custom,
        name: result.name,
        status: FoodStatus.Custom,
        nutrients: result.nutrients,
        weight_g: 100,
      };

      // add to meal ingredients, keeping a copy of the list so it can be put back if the meal
      // turns out not to be savable (see tryRecalculateMealNutrientsAsync)
      const ingredients = this.ingredientsOf(meal);
      const previousIngredients = ingredients.slice();
      ingredients.push(newIngredient);

      // recalculate meal nutrients
      if (!await this.tryRecalculateMealNutrientsAsync(meal)) {
        meal.ingredients = previousIngredients;
        return;
      }

      // save meal
      const transaction = DatabaseWrapper.getTransactionId();
      await this.userDb.saveEntryAsync(transaction, meal);
      await this.userDb.updateDayValuesOnDiskAsync(transaction, getEntryLocalTimeInDesiredTimeZone(meal, this.debug.getNowLocal()));

      // reload schedule to show changes
      await this.reloadScheduleAsync();

      this.telemetry.trackEvent('day_items_log_macros_manually', {});
    }
  }

  async handleMealPlannerActionClicked(item: ScheduleItem) {
    const meal = item.entry as Log_Meal;

    this.telemetry.trackEvent('day_items_meal_planner', {});

    // Working out what to suggest reads the day from the database, so it can fail. The popup-menu
    // handler that calls this discards the returned promise, so without this the menu item would
    // simply do nothing, with no word to the user — so the read reports its own failure.
    const tuningTargets = await this.userDb.getMealTargetsAsIfEmptyOrReportAsync(
      meal, LL.editMeal.errors.couldNotSizeSuggestions());
    if (!tuningTargets) return;
    if (this.destroyGuard.isDestroyed) return;

    const hasIngredients = this.ingredientsOf(meal).length > 0;
    const params: MealSuggestionsPageParams = {
      title: formatScheduledMealName(meal.name),
      mode: hasIngredients ? MealSuggestionsMode.Replace : MealSuggestionsMode.Add,
      returnCallback: 'onMealSuggestionResult',
      local: {
        targets: meal.targets,
        // What a suggested meal should contain — see `mealTargetsAsIfEmpty` (#5297).
        tuningTargets,
        returnContext: { mealId: meal._id },
      },
    };

    void this.navCtrl.pushAsync(IMealSuggestionsPage, params);
  }

  // navigate to meal photo scanner for an empty meal
  handleScanMealClicked(item: ScheduleItem) {
    const meal = item.entry as Log_Meal;
    const ingredients = this.ingredientsOf(meal);
    const hasIngredients = ingredients.length > 0;
    this.telemetry.trackEvent('meal_photo_scan_open', { source: 'day_items' });
    void this.navCtrl.pushAsync(IMealPhotoScannerPage, {
      mode: hasIngredients ? 'replace' : 'add',
      returnCallback: 'onMealPhotoScanResult',
      local: {
        returnContext: { mealId: meal._id },
        // forwarded by the scanner to the portion screen it opens, so that screen's macro
        // card can offer its `Day` and meal scopes. The ingredients are sent whatever the
        // mode is: the scanner decides whether they survive, from its own mode -- deciding
        // it here as well would be two copies of one rule, and the copy that drifted would
        // silently double-count a replaced meal.
        ...mealPhotoScanMealParams(meal, ingredients, this.debug.getNowLocal()),
      },
    });
  }

  // callback from meal photo scanner — replace or add scanned foods as ingredients
  async onMealPhotoScanResult(context: { mealId: string }, result: MealPhotoScannerResult) {
    if (!result?.matchedFoods?.length) return;

    const item = this.schedule.find(s => s.entry._id === context.mealId);
    if (!item) return;
    const meal = item.entry as Log_Meal;

    const existingIngredients = this.ingredientsOf(meal);

    // keep a copy of the list so it can be put back if the meal turns out not to be savable
    // (see tryRecalculateMealNutrientsAsync)
    const previousIngredients = existingIngredients.slice();

    // if meal already has ingredients, replace them. either way `scannedInto` is the list the
    // scanned foods go into, so the loop below never reads the meal's field again.
    let scannedInto = existingIngredients;
    if (existingIngredients.length > 0) {
      scannedInto = [];
      meal.ingredients = scannedInto;
    }

    for (const matched of result.matchedFoods) {
      const food = await this.foodDb.getFoodByIdAsync(matched.food._id, { cacheFood: true, onReadFailure: FoodReadFailurePolicy.TreatAsMissing });
      if (!food) continue;

      const ingredient: IngredientReal = {
        type: IngredientType.Real,
        foodId: food._id,
        name: food.name,
        weight_g: matched.estimatedGrams,
        // keep the "Weighed with peel" tick from the scanner's portion screen (#5544)
        ...(matched.weighedWithRefuse ? { weighedWithRefuse: matched.weighedWithRefuse } : {}),
      };
      this.foodDb.setIngredientStatus(ingredient);
      scannedInto.push(ingredient);
      void this.foodDb.markFoodAsUsedAsync(food);
    }

    // recalculate and save
    if (!await this.tryRecalculateMealNutrientsAsync(meal)) {
      meal.ingredients = previousIngredients;
      return;
    }
    const transaction = DatabaseWrapper.getTransactionId();
    await this.userDb.saveEntryAsync(transaction, meal);
    await this.userDb.updateDayValuesOnDiskAsync(transaction, getEntryLocalTimeInDesiredTimeZone(meal, this.debug.getNowLocal()));
    await this.reloadScheduleAsync();
    this.destroyGuard.safeMarkForCheck(this.cdRef);
  }

  // redirect from the meal photo scanner's packaged-product alert (#3902): the
  // scanner replaced itself with the Foods page for a barcode / nutrition-label
  // scan, and a food came back. Open the portion screen on it, for the target
  // meal (context.mealId), and let that screen write the day — this method
  // persists nothing itself. It appended the food at a flat 100 g and saved the
  // meal here until #4968; the body below says why that had to stop.
  //
  // It deliberately does NOT reuse onMealPhotoScanResult, which is unchanged and
  // still does its own writing: that handler wipes an already-populated meal (it
  // is for re-scanning a whole meal photo), and a single packaged-product add
  // must never discard the meal's existing ingredients.
  async onMealScanRedirectFood(context: { mealId: string }, result: FoodsPageResult) {
    // the row tap ('select') is the only result that carries a foodId. The per-card "+"
    // button used to produce a second one ('quickAdd'); that button was removed in #4967.
    const foodId = result?.action === 'select' ? result.foodId : undefined;
    if (!foodId) {
      // 'addMacros' (custom-macro entry) has no food to add — surface rather than swallow
      if (result?.action === 'addMacros') {
        this.logging.trackSilentProblem('MealScanRedirectAddMacrosUnsupported', { mealId: context.mealId });
      }
      return;
    }

    const item = this.schedule.find(s => s.entry._id === context.mealId);
    if (!item) return;
    const meal = item.entry as Log_Meal;

    // Ask how much, instead of deciding for the user (#4968).
    //
    // This used to append the food at a flat 100 g and save the meal itself, which is the one
    // thing this ticket exists to stop: a food entering a day without the user ever seeing the
    // amount. It was already wrong when the user had at least PICKED the food off a list; since
    // this branch, a food created by a label scan is delivered here automatically, so nothing
    // asked them anything at all.
    //
    // The portion screen in Log mode writes the day itself — it saves the meal, announces the
    // change and offers an Undo — so there is nothing to persist here and no result to wait
    // for. `selectedMealId` is what keeps the food in the meal the scan was started from
    // rather than the day's default one.
    const mealDate = getEntryLocalTimeInDesiredTimeZone(meal, this.debug.getNowLocal());
    await this.navCtrl.pushAsync(IEditIngredientPage, buildLogPortionScreenParams({
      dayDate: mealDate.toISODate(),
      selectedMealId: meal._id,
      // the food list answers with a RECENT MEAL as well as a food -- meal rows are selectable
      // in select mode, and their ids read `log/meal/...` rather than `food/...`. Which
      // parameter that becomes is decided inside the helper.
      pickedId: foodId,
    }));
  }

  private async handleClearMealButton(item: ScheduleItem) {
    const meal = item.entry as Log_Meal;
    const cleared = await this.mealActionsService.clearMealAsync(meal);
    if (cleared) {
      // reload schedule to show changes (state changes from Planned to Scheduled)
      await this.reloadScheduleAsync();
      this.destroyGuard.safeMarkForCheck(this.cdRef);
    }
  }

  /**
   * Callback invoked by NavController when returning from MealSuggestionsPage.
   * This is called via the returnCallback mechanism in nav-controller.ts.
   */
  async onMealSuggestionResult(context: { mealId: string }, suggestion: MealTunerMeal | undefined) {
    if (suggestion) {
      // find the meal in the schedule
      const item = this.schedule.find(s => s.entry._id === context.mealId);
      if (!item) {
        return;
      }
      const meal = item.entry as Log_Meal;

      // keep a copy of what the suggestion is about to replace, so it can be put back if the
      // meal turns out not to be savable (see tryRecalculateMealNutrientsAsync).
      // basedOnFoodId is in here because fillMealFromTunerAsync sets it too — leaving it behind
      // would point the meal at a suggestion the user never actually got.
      const previousIngredients = this.ingredientsOf(meal).slice();
      const previousAutoPlanned = meal.autoPlanned;
      const previousBasedOnFoodId = meal.basedOnFoodId;

      // fill meal from tuner suggestion
      await this.foodDb.fillMealFromTunerAsync(meal, suggestion);
      meal.autoPlanned = true;

      // recalculate meal nutrients
      if (!await this.tryRecalculateMealNutrientsAsync(meal)) {
        meal.ingredients = previousIngredients;
        meal.autoPlanned = previousAutoPlanned;
        meal.basedOnFoodId = previousBasedOnFoodId;
        return;
      }

      // save meal
      const transaction = DatabaseWrapper.getTransactionId();
      await this.userDb.saveEntryAsync(transaction, meal);
      await this.userDb.updateDayValuesOnDiskAsync(transaction, getEntryLocalTimeInDesiredTimeZone(meal, this.debug.getNowLocal()));

      // mark meal as used
      await this.foodDb.markMealAsUsedAsync(meal);

      // reload schedule to show changes
      await this.reloadScheduleAsync();

      this.destroyGuard.safeMarkForCheck(this.cdRef);
    }
    // if user cancelled, no action needed
  }

  async handleRescheduleButton(item: ScheduleItem, targetElement: HTMLElement) {
    const entry = item.entry;

    // get meal index and all meal times if it's a meal
    let mealIndex: number | undefined;
    let allMealTimes_hr: number[] | undefined;
    if (entry.type === LogRecordType.Meal) {
      const now = this.debug.getNowLocal();
      const dayMeals = this.schedule
        .filter(s => s.entry.type === LogRecordType.Meal)
        .sort((a, b) => a.entry.time_utc.localeCompare(b.entry.time_utc));
      mealIndex = dayMeals.findIndex(m => m.entry._id === entry._id);
      allMealTimes_hr = dayMeals.map(m => isoTimeToHours(getEntryLocalTimeInDesiredTimeZone(m.entry, now).toISOTime()));
    }

    // prepare params for popover
    const entryType = entry.type as SchedulableLogRecordType;
    const params: ReschedulePopoverParams = {
      mode: RescheduleMode.LogEntry,
      entryType,
      entry,
      mealIndex,
      allMealTimes_hr,
    };

    // open ReschedulePopover
    const result = await ReschedulePopover.present(
      this.popoverCtrl,
      targetElement,
      params
    );

    if (result && result.success) {
      // reload the schedule to show changes
      await this.reloadScheduleAsync();
      this.destroyGuard.safeMarkForCheck(this.cdRef);

      this.telemetry.trackEvent('day_items_reschedule', {
        itemType: entry.type
      });
    }
  }


  /**
   * Work out what the weigh-in card should say about the user's weekly weight
   * change. Gathers the last 14 days of weigh-ins (one per day) and hands them
   * to the shared `estimateWeeklyWeightChangeForDisplay`, which applies the same
   * Theil-Sen fit as cs-trend-chart PLUS the plan engine's minimum-sample gate.
   * The card then shows either the weekly rate, an "undecided" message when
   * there are too few weigh-ins (#4306), or nothing.
   */
  private async calculateWeightProgress(currentDay: DateTime, currentWeighIn: Log_WeighIn) {
    // reset the undecided flag before recomputing; showWeightProgress is set
    // explicitly in each branch below
    this.showWeightTrendUndecided = false;
    const now = this.debug.getNowLocal();
    const untilDate = currentDay.endOf('day');
    const dataCollectionStartDate = untilDate.minus({ days: 13 }).startOf('day'); // Collect 14 days of data

    // Get last 14 days of log entries
    const logEntries = await this.userDb.getLogEntriesInRangeAsync(dataCollectionStartDate, untilDate, this.docWatcher, { inLogEntriesTimeZone: true });
    const weighInsByDay: { [date: string]: Log_WeighIn } = {};
    for (const entry of logEntries.filter(e => e.type === LogRecordType.WeighIn)) {
      const d = getEntryLocalTimeInDesiredTimeZone(entry, now).toISODate();
      weighInsByDay[d] = entry as Log_WeighIn;
    }

    // Create a sorted list of all 14 days
    const allDays: string[] = [];
    for (let d = dataCollectionStartDate; d <= untilDate; d = d.plus({ days: 1 })) {
      allDays.push(d.toISODate());
    }

    // Collect one weight value per day (undefined for days with no weigh-in)
    const dailyWeights_kg: (number | undefined)[] = [];
    for (const day of allDays) {
      const weighIn = weighInsByDay[day];
      if (weighIn && weighIn.weight_kg && weighIn.weight_kg > 0) {
        dailyWeights_kg.push(weighIn.weight_kg);
      } else {
        dailyWeights_kg.push(undefined);
      }
    }

    // Estimate the weekly change with the same min-sample gate the plan engine
    // uses (shared helper). This is the #4306 fix: two back-to-back weigh-ins
    // are not enough to state a confident weekly rate.
    const trend = estimateWeeklyWeightChangeForDisplay(dailyWeights_kg);

    // too few weigh-ins to trust a trend — show the "undecided" message
    if (trend.kind === WeightTrendKind.Insufficient) {
      this.showWeightTrendUndecided = true;
      this.showWeightProgress = false;
      return;
    }

    // nothing meaningful to show (fewer than 2 weigh-ins or a negligible change)
    if (trend.kind === WeightTrendKind.None) {
      this.showWeightProgress = false;
      return;
    }

    const weeklyChange_kg = trend.weeklyChange_kg;

    // Convert to display units
    const { factor: weightUnitFactor, unitName: weightUnitName } = getMeasureUnitConversion(
      'bodyweight_kg',
      this.settings.locale,
      currentWeighIn.weight_unit
    );

    this.weightProgressAmount = roundToPrecision(Math.abs(weeklyChange_kg * weightUnitFactor), 0.1);
    this.weightProgressDirection = weeklyChange_kg < 0 ? 'down' : 'up';
    this.weightProgressUnit = weightUnitName;
    this.showWeightProgress = true;

    // check if user is on track with their progress
    let checkResult: CheckForPlanAdjustmentsResult;
    try {
      checkResult = await this.svPlanHandler.checkForPlanAdjustments();
    } catch (error) {
      // the plan check could not run, so there is no verdict to colour this alert with. The
      // alert carries BOTH the on-track colour and the weekly-change figure, so hiding it takes
      // the figure with it -- which is the right trade: an alert whose colour says "on track"
      // when nothing worked that out is worse than no alert (df-5ba092626ea2). The service has
      // already reported the failure; anything else here is a surprise and goes up.
      if (!isPlanCheckFailed(error)) throw error;
      this.showWeightProgress = false;
      return;
    }
    this.isOnTrackWithProgress = !checkResult.needsAdjustment;
    this.isProgressIndeterminate = !checkResult.needsAdjustment && checkResult.message !== PlanUpdateUserMessage.AllOk;
  }

}

